import React, { useState } from "react";

import { useNavigate, useSearchParams } from "react-router-dom";

import Typography from "@mui/material/Typography";

import BillingLink from "@app/analytics/account/Billing/components/BillingLink";
import BillingLinkButton from "@app/analytics/account/Billing/components/BillingLinkButton";
import BillingModuleError from "@app/analytics/account/Billing/components/BillingModuleError";
import {
  BillingCard,
  BillingCardActions,
  BillingCardContent,
  BillingCardHeader,
  LicensesUsedProgress,
} from "@app/analytics/account/Billing/components/styles";
import usePaymentMethod from "@app/analytics/account/Billing/hooks/usePaymentMethod";
import {
  isZoomPlan,
  subscriptionWillCancel,
} from "@app/analytics/account/Billing/utils";
import useBillingContext from "@app/analytics/hooks/billing/useBillingContext";
import ROUTES from "@app/common/routes";
import { pluralizePhrase, withoutQueryParams } from "@app/common/utils";
import useWorkspace from "@app/common/workspaces/useWorkspace";

import Stack, { Row } from "@rui/Stack";
import TooltipIcon from "@rui/TooltipIcon";

import AddLicensesModal from "./AddLicensesModal";
import RemoveLicensesModal from "./RemoveLicensesModal";

const NO_LICENSES_AVAILABLE_TOOLTIP = {
  noVacancy:
    "You already have the maximum number of licenses as allowed by your plan. Upgrade to Read Pro or Enterprise to add more.",
  noPaymentMethod:
    "You must add a payment method to purchase additional licenses",
};

const NO_UNUSED_LICENSES_TOOLTIP = {
  withWorkspace:
    "You have no unused licenses in your Workspace. To remove licenses, first remove people by clicking “Manage people” above.",
  withoutWorkspace: "You have no unused licenses",
};

const SCHEDULED_LICENSE_REMOVAL_TOOLTIP =
  "You can use these licenses at no additional cost up through the remainder of the current billing period. If these licenses are in use next billing period, you will be charged the normal rate.";

const LicenseUsage = () => {
  const navigate = useNavigate();
  const [queryParams] = useSearchParams();
  const { inWorkspace, workspaceId } = useWorkspace();
  const { licenses, subscription, subscriptionIsError } = useBillingContext();
  const { hasPaymentMethod } = usePaymentMethod();

  const [removeLicensesOpen, setRemoveLicensesOpen] = useState(false);
  const [addLicensesOpen, setAddLicensesOpen] = useState(
    queryParams.get("add_licenses") === "true"
  );

  const subscriptionAllowsLicenseManagementWithoutPaymentMethod =
    subscription.inTrialPeriod || isZoomPlan(subscription.plan);

  // don't bother even showing the button if they're occupying the only license they've paid for, or if
  // the subscription is already set to cancel (and they're not in a trial/zoom plan)
  const showRemoveLicensesButton =
    licenses.numActive > 1 &&
    (!subscriptionWillCancel(subscription) ||
      subscriptionAllowsLicenseManagementWithoutPaymentMethod);

  const atLicenseLimit = (licenses.numAvailableForPurchase ?? 1) <= 0;

  // users can add licenses if they have a payment method on file, if they're on a free trial,
  // or if they are on the zoom plan and have not yet maxed out their licenses
  const canAddLicenses =
    (hasPaymentMethod ||
      subscriptionAllowsLicenseManagementWithoutPaymentMethod) &&
    !atLicenseLimit;
  const cannotPurchaseLicenses =
    !canAddLicenses && licenses.numActive == licenses.numPaid;

  const disableAddLicensesButton = atLicenseLimit || cannotPurchaseLicenses;

  const toggleAddLicensesModal = () => {
    setAddLicensesOpen((open) => !open);
    if (queryParams.get("add_licenses")) {
      navigate(
        withoutQueryParams(location.pathname, queryParams, ["add_licenses"]),
        { replace: true }
      );
    }
  };

  const toggleRemoveLicensesModal = () =>
    setRemoveLicensesOpen((open) => !open);
  const percentLicensesUsed = licenses.numClaimed / licenses.numActive;

  return (
    <>
      <BillingCard>
        <BillingCardHeader>
          <Typography variant="bodyPrimaryMed">License Usage</Typography>
          {workspaceId ? (
            <BillingLink href={ROUTES.workspaceSettingsPeopleById(workspaceId)}>
              Manage people
            </BillingLink>
          ) : licenses.numUnclaimed > 0 ? (
            <BillingLink href={ROUTES.NEW_WORKSPACE}>
              Create Workspace
            </BillingLink>
          ) : null}
        </BillingCardHeader>
        <BillingCardContent spacing={1.5}>
          {subscriptionIsError ? (
            <BillingModuleError entityLabel="licenses" />
          ) : (
            <>
              <Stack spacing={1.5}>
                <Typography variant="bodyPrimaryReg">
                  {licenses.numClaimed} of{" "}
                  {pluralizePhrase(licenses.numActive, "license is", {
                    plural: "licenses are",
                  })}{" "}
                  in use
                </Typography>
                <LicensesUsedProgress
                  error={percentLicensesUsed > 1}
                  value={Math.min(100, percentLicensesUsed * 100)}
                />
                {licenses.numInactive > 0 && (
                  <Row variant="left" spacing={1}>
                    <Typography
                      variant="labelReg"
                      sx={{ color: "shades.neutral.500", lineHeight: 1 }}
                    >
                      {pluralizePhrase(
                        licenses.numInactive,
                        "additional license is",
                        { plural: "additional licenses are" }
                      )}{" "}
                      scheduled to be removed at the end of this billing period.
                    </Typography>
                    <TooltipIcon
                      title={SCHEDULED_LICENSE_REMOVAL_TOOLTIP}
                      placement="bottom-end"
                    />
                  </Row>
                )}
              </Stack>
              <BillingCardActions>
                <Row spacing={1}>
                  <BillingLinkButton
                    onClick={toggleAddLicensesModal}
                    disabled={disableAddLicensesButton}
                    disabledTooltip={
                      atLicenseLimit
                        ? NO_LICENSES_AVAILABLE_TOOLTIP.noVacancy
                        : NO_LICENSES_AVAILABLE_TOOLTIP.noPaymentMethod
                    }
                  >
                    Add licenses
                  </BillingLinkButton>
                  {showRemoveLicensesButton && (
                    <BillingLinkButton
                      color="error"
                      variant="text"
                      disabled={licenses.numUnclaimed < 1}
                      disabledTooltip={
                        inWorkspace
                          ? NO_UNUSED_LICENSES_TOOLTIP.withWorkspace
                          : NO_UNUSED_LICENSES_TOOLTIP.withoutWorkspace
                      }
                      onClick={toggleRemoveLicensesModal}
                    >
                      Remove licenses
                    </BillingLinkButton>
                  )}
                </Row>
              </BillingCardActions>
            </>
          )}
        </BillingCardContent>
      </BillingCard>
      {!subscriptionIsError && (
        <>
          {/* Modals */}
          <RemoveLicensesModal
            open={removeLicensesOpen}
            onClose={toggleRemoveLicensesModal}
            workspaceId={workspaceId}
          />
          <AddLicensesModal
            open={addLicensesOpen}
            onClose={toggleAddLicensesModal}
            workspaceId={workspaceId}
          />
        </>
      )}
    </>
  );
};

export default LicenseUsage;
